import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { TrendingUp, Filter, RefreshCw, ChevronDown, ChevronUp, User, X } from 'lucide-react';
import { useAuthStore } from '../store';
import toast from 'react-hot-toast';

export default function PipelinePage() {
  const { user } = useAuthStore();
  const canFilterByStaff = ['super_admin', 'dept_admin'].includes(user?.role);

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedLead, setExpandedLead] = useState(null);
  const [staffList, setStaffList] = useState([]);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [submitterId, setSubmitterId] = useState('');

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { source: 'public_lead', limit: 200 };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (submitterId) params.submitter_id = submitterId;
      const res = await axios.get('/api/submissions', { params });
      setLeads(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, submitterId]);

  useEffect(() => {
    loadLeads();
    if (canFilterByStaff) {
      axios.get('/api/auth/users').then(res => setStaffList(res.data || [])).catch(() => {});
    }
  }, []);

  const getSchema = (lead) => {
    const schema = typeof lead.form_schema === 'string' ? JSON.parse(lead.form_schema) : (lead.form_schema || {});
    const all = schema.fields || [];
    return {
      customerFields: all.filter(f => !f.visibility || f.visibility === 'customer' || f.visibility === 'both'),
      staffFields: all.filter(f => f.visibility === 'staff'),
    };
  };

  const getDisplayName = (lead, customerFields) => {
    const data = lead.data || {};
    for (const f of customerFields) {
      if (f.label.includes('姓') || f.label.includes('名') || f.label.toLowerCase().includes('name')) {
        if (data[f.id]) return data[f.id];
      }
    }
    return Object.values(data)[0] || lead.form_title || '—';
  };

  const hasFilters = fromDate || toDate || submitterId;

  return (
    <div style={{ padding: 32 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>業務管線</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 14, marginTop: 4 }}>共 {total} 筆商機</p>
        </div>
        <button onClick={loadLeads} className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <RefreshCw size={14} /> 重新整理
        </button>
      </div>

      {/* Filter Bar */}
      <div className="card" style={{ padding: '14px 16px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>建檔日期 從</label>
          <input type="date" className="input" value={fromDate}
            onChange={e => setFromDate(e.target.value)} style={{ fontSize: 13, width: 148 }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>至</label>
          <input type="date" className="input" value={toDate}
            onChange={e => setToDate(e.target.value)} style={{ fontSize: 13, width: 148 }} />
        </div>
        {canFilterByStaff && staffList.length > 0 && (
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>業務人員</label>
            <select className="input" value={submitterId}
              onChange={e => setSubmitterId(e.target.value)} style={{ fontSize: 13, width: 150 }}>
              <option value="">全部</option>
              {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        <button onClick={loadLeads} className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Filter size={14} /> 套用篩選
        </button>
        {hasFilters && (
          <button onClick={() => { setFromDate(''); setToDate(''); setSubmitterId(''); }}
            className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <X size={14} /> 清除
          </button>
        )}
      </div>

      {/* Lead List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>載入中...</div>
      ) : leads.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <TrendingUp size={40} color="var(--text-3)" style={{ marginBottom: 12 }} />
          <p style={{ color: 'var(--text-2)', fontSize: 15 }}>
            {hasFilters ? '沒有符合篩選條件的商機' : '尚無商機資料'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {leads.map(lead => {
            const isExpanded = expandedLead === lead.id;
            const { customerFields, staffFields } = getSchema(lead);
            const data = lead.data || {};
            const displayName = getDisplayName(lead, customerFields);

            return (
              <div key={lead.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                  onClick={() => setExpandedLead(isExpanded ? null : lead.id)}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <User size={16} color="#1a56db" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {displayName}
                      <span className="badge badge-green" style={{ marginLeft: 8, fontSize: 10 }}>{lead.form_title}</span>
                      {lead.submitter_name && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-3)', fontWeight: 400 }}>
                          業務：{lead.submitter_name}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-2)', marginTop: 2, flexWrap: 'wrap' }}>
                      {customerFields.slice(0, 3).map(f => {
                        const v = data[f.id];
                        return v ? <span key={f.id}>{f.label}: {v}</span> : null;
                      }).filter(Boolean)}
                      {staffFields.slice(0, 2).map(f => {
                        const v = data[f.id];
                        return v ? <span key={f.id} style={{ color: '#1a56db' }}>• {f.label}: {v}</span> : null;
                      }).filter(Boolean)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                      {new Date(lead.submitted_at).toLocaleDateString('zh-TW')}
                    </span>
                    {isExpanded ? <ChevronUp size={16} color="var(--text-3)" /> : <ChevronDown size={16} color="var(--text-3)" />}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ padding: '0 18px 16px', borderTop: '1px solid var(--border)' }}>
                    {customerFields.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: 8 }}>客戶填寫</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                          {customerFields.map(f => (
                            <div key={f.id} style={{ fontSize: 13, padding: '4px 0' }}>
                              <span style={{ color: 'var(--text-3)' }}>{f.label}：</span>
                              <span style={{ fontWeight: 500 }}>{data[f.id] || '—'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {staffFields.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', marginBottom: 8 }}>業務欄位</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                          {staffFields.map(f => (
                            <div key={f.id} style={{ fontSize: 13, padding: '4px 0' }}>
                              <span style={{ color: 'var(--text-3)' }}>{f.label}：</span>
                              <span style={{ fontWeight: 500, color: 'var(--primary)' }}>{data[f.id] || '—'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
